import React, { useState, useEffect, useMemo } from 'react';
import { X, Zap, Loader2, RefreshCw, CheckCircle2, Clock, AlertCircle, HelpCircle, Sparkles, Brain, List, History, Check, XCircle, ChevronDown } from 'lucide-react';
import { ReviewQuestion, ReviewAttempt, Language, MindMapNode } from '../types';
import { loadQuestionsForNode, loadAttemptsForNode } from '../services/reviewFeedService';

interface NoteQuestionsModalProps {
    node: MindMapNode;
    userId: string;
    language: Language;
    onClose: () => void;
    onGenerateMore: (nodeId: string) => void;
    isGenerating?: boolean;
    onOpenReviewFeed?: () => void;
}

type ActiveTab = 'questions' | 'history';

type HistoryEvent =
  | { type: 'ai_check'; timestamp: number; score: number }
  | { type: 'questions_generated'; timestamp: number; count: number; origin: string }
  | { type: 'memory_gym'; timestamp: number; score: number }
  | { type: 'review_attempt'; timestamp: number; correct: boolean; questionText: string };

// --- helpers ---

const StatusChip: React.FC<{ q: ReviewQuestion }> = ({ q }) => {
    const now = Date.now();
    if (q.timesAsked === 0) return (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            <HelpCircle size={9} /> NEW
        </span>
    );
    if (!q.nextDueAt || q.nextDueAt <= now) {
        const ratio = q.timesAsked > 0 ? q.timesCorrect / q.timesAsked : 0;
        if (ratio < 0.5) return (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                <AlertCircle size={9} /> WEAK
            </span>
        );
        return (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                <Clock size={9} /> DUE
            </span>
        );
    }
    const diffD = Math.ceil((q.nextDueAt - now) / (1000 * 60 * 60 * 24));
    return (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
            <CheckCircle2 size={9} /> {diffD}d
        </span>
    );
};

const originLabel = (origin: string, language: Language): string => {
    const map: Record<string, [string, string]> = {
        initial_ai_check: ['AI Check', 'AI Check'],
        active_review:    ['Memory Gym', 'Memory Gym'],
        source:           ['Tài liệu gốc', 'Source'],
        manual_generation:['Thủ công', 'Manual'],
        regenerated:      ['Tạo lại', 'Regenerated'],
        wrong_answer:     ['Điểm yếu', 'Weakness'],
    };
    const pair = map[origin];
    if (!pair) return origin;
    return language === 'vietnamese' ? pair[0] : pair[1];
};

const generationLabel = (origin: string, language: Language): string => {
    const map: Record<string, [string, string]> = {
        initial_ai_check: ['sau AI Check', 'after AI Check'],
        active_review:    ['từ Memory Gym', 'from Memory Gym'],
        source:           ['từ nguồn', 'from source'],
        manual_generation:['tạo thủ công', 'manual'],
        regenerated:      ['tạo lại', 'regenerated'],
        wrong_answer:     ['từ điểm yếu', 'from weakness'],
    };
    const pair = map[origin];
    if (!pair) return origin;
    return language === 'vietnamese' ? pair[0] : pair[1];
};

const formatRelTime = (ts: number, language: Language): string => {
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (language === 'vietnamese') {
        if (m < 1) return 'Vừa xong';
        if (m < 60) return `${m} phút trước`;
        if (h < 24) return `${h} giờ trước`;
        if (d < 7) return `${d} ngày trước`;
        return new Date(ts).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    } else {
        if (m < 1) return 'Just now';
        if (m < 60) return `${m}m ago`;
        if (h < 24) return `${h}h ago`;
        if (d < 7) return `${d}d ago`;
        return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
};

const scoreColor = (score: number) =>
    score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
    score >= 50 ? 'text-amber-600 dark:text-amber-400' :
                  'text-red-600 dark:text-red-400';

const InfoTip: React.FC<{ label: string; body: string }> = ({ label, body }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="px-5 py-3 border-t dark:border-slate-700/50">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1.5 text-[10px] text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
                <HelpCircle size={10} />
                <span>{label}</span>
                <ChevronDown size={9} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                    {body}
                </p>
            )}
        </div>
    );
};

// --- main component ---

export const NoteQuestionsModal: React.FC<NoteQuestionsModalProps> = ({
    node, userId, language, onClose, onGenerateMore, isGenerating = false, onOpenReviewFeed,
}) => {
    const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
    const [attempts, setAttempts] = useState<ReviewAttempt[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<ActiveTab>('questions');

    useEffect(() => {
        Promise.all([
            loadQuestionsForNode(userId, node.id),
            loadAttemptsForNode(userId, node.id),
        ]).then(([qs, as]) => {
            setQuestions(qs);
            setAttempts(as);
            setLoading(false);
        });
    }, [userId, node.id]);

    const now = Date.now();

    // Overview stats
    const dueCount = questions.filter(q => !q.nextDueAt || q.nextDueAt <= now).length;
    const newCount = questions.filter(q => q.timesAsked === 0).length;
    const totalAnswered = questions.reduce((s, q) => s + q.timesAsked, 0);
    const totalCorrect = questions.reduce((s, q) => s + q.timesCorrect, 0);
    const accuracy = totalAnswered > 0 ? Math.round(totalCorrect / totalAnswered * 100) : null;
    const nextDueFuture = questions
        .filter(q => q.nextDueAt && q.nextDueAt > now)
        .map(q => q.nextDueAt as number);
    const nextDueAt = nextDueFuture.length > 0 ? Math.min(...nextDueFuture) : null;

    // Build history events
    const historyEvents = useMemo<HistoryEvent[]>(() => {
        const events: HistoryEvent[] = [];

        // AI Check event
        if (node.lastAICheckAt) {
            events.push({ type: 'ai_check', timestamp: node.lastAICheckAt, score: node.analysis?.score ?? 0 });
        }

        // Question generation batches (group by 10-second windows)
        const byBatch = new Map<number, ReviewQuestion[]>();
        for (const q of questions) {
            const key = Math.floor((q.generatedAt ?? 0) / 10000);
            if (!byBatch.has(key)) byBatch.set(key, []);
            byBatch.get(key)!.push(q);
        }
        for (const [, group] of byBatch) {
            events.push({
                type: 'questions_generated',
                timestamp: group[0].generatedAt ?? 0,
                count: group.length,
                origin: group[0].origin,
            });
        }

        // Memory Gym sessions
        for (const session of node.reviewSessions ?? []) {
            events.push({
                type: 'memory_gym',
                timestamp: new Date(session.timestamp).getTime(),
                score: session.score,
            });
        }

        // Review Feed attempts
        const questionMap = new Map<string, string>(questions.map(q => [q.id, q.question]));
        for (const attempt of attempts) {
            events.push({
                type: 'review_attempt',
                timestamp: attempt.createdAt,
                correct: attempt.correct,
                questionText: questionMap.get(attempt.questionId) ?? '—',
            });
        }

        return events.sort((a, b) => b.timestamp - a.timestamp);
    }, [node, questions, attempts]);

    const vi = language === 'vietnamese';

    return (
        <div
            className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                style={{ maxHeight: '85vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between px-5 pt-5 pb-3 flex-shrink-0">
                    <div>
                        <div className="flex items-center gap-2 mb-0.5">
                            <Zap size={15} className="text-amber-500" />
                            <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                                {vi ? 'Câu hỏi ôn tập' : 'Review Questions'}
                            </span>
                        </div>
                        <h2 className="text-sm font-bold text-gray-800 dark:text-white leading-snug line-clamp-2">{node.title}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full flex-shrink-0 ml-3"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Overview stats */}
                {!loading && (
                    <div className="px-5 pb-3 flex-shrink-0">
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {/* AI Check score */}
                            {node.analysis && (
                                <div className="flex items-center gap-1.5">
                                    <Sparkles size={11} className="text-purple-400" />
                                    <span className={`text-[11px] font-bold ${scoreColor(node.analysis.score)}`}>
                                        {node.analysis.score}/100
                                    </span>
                                    <span className="text-[11px] text-gray-400">AI Check</span>
                                </div>
                            )}
                            {/* Questions total */}
                            {questions.length > 0 && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200">{questions.length}</span>
                                    <span className="text-[11px] text-gray-400">{vi ? 'câu' : 'questions'}</span>
                                </div>
                            )}
                            {/* Accuracy */}
                            {accuracy !== null && (
                                <div className="flex items-center gap-1">
                                    <span className={`text-[11px] font-bold ${scoreColor(accuracy)}`}>{accuracy}%</span>
                                    <span className="text-[11px] text-gray-400">{vi ? 'chính xác' : 'accuracy'}</span>
                                </div>
                            )}
                            {/* Due */}
                            {dueCount > 0 && (
                                <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                                    {dueCount} {vi ? 'cần ôn' : 'due'}
                                </span>
                            )}
                            {/* New */}
                            {newCount > 0 && (
                                <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                                    {newCount} {vi ? 'mới' : 'new'}
                                </span>
                            )}
                            {/* Next due date */}
                            {nextDueAt && dueCount === 0 && (() => {
                                const diffD = Math.ceil((nextDueAt - now) / 86400000);
                                return (
                                    <span className="text-[11px] text-blue-500 dark:text-blue-400">
                                        {vi ? `Ôn lại: ${diffD}ngày nữa` : `Next: in ${diffD}d`}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* Tab bar */}
                <div className="flex border-b dark:border-slate-700 flex-shrink-0 px-4">
                    <button
                        onClick={() => setActiveTab('questions')}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                            activeTab === 'questions'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                    >
                        <List size={12} />
                        {vi ? 'Câu hỏi' : 'Questions'}
                        {!loading && questions.length > 0 && (
                            <span className="text-[10px] bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 rounded-full px-1.5 py-0.5 font-bold">
                                {questions.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                            activeTab === 'history'
                                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                    >
                        <History size={12} />
                        {vi ? 'Lịch sử' : 'History'}
                    </button>
                </div>

                {/* Tab content */}
                <div className="flex-1 overflow-y-auto">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={24} className="animate-spin text-amber-500" />
                        </div>
                    )}

                    {/* Questions tab */}
                    {!loading && activeTab === 'questions' && (
                        <>
                            {questions.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-10 px-5 gap-2 text-center">
                                    <p className="text-sm text-gray-400">
                                        {vi ? 'Chưa có câu hỏi nào cho ghi chú này.' : 'No questions yet for this note.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="py-2">
                                    {questions.map((q, i) => (
                                        <div key={q.id} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                                            <span className="text-[11px] font-bold text-gray-300 dark:text-slate-600 w-4 flex-shrink-0 mt-0.5">{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-700 dark:text-gray-200 leading-snug line-clamp-2 mb-1.5">{q.question}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-gray-400">
                                                        {originLabel(q.origin ?? q.questionOrigin ?? '', language)}
                                                    </span>
                                                    {q.timesAsked > 0 && (
                                                        <span className="text-[10px] text-gray-400">
                                                            · {q.timesCorrect}/{q.timesAsked}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <StatusChip q={q} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        <InfoTip
                            label={vi ? 'Câu hỏi này dùng để làm gì?' : 'What are these questions for?'}
                            body={vi
                                ? 'Đây là các câu hỏi trắc nghiệm sẽ xuất hiện trong Review Feed của bạn. Hệ thống ưu tiên câu mới, câu yếu và câu đến hạn ôn — giúp bạn ghi nhớ lâu hơn với ít thời gian hơn.'
                                : 'These questions appear in your Review Feed. They\'re prioritized by what you need most — new, weak, and due questions come first — so you retain more with less study time.'
                            }
                        />
                        {onOpenReviewFeed && questions.length > 0 && (
                            <div className="px-5 pb-3">
                                <button
                                    onClick={() => { onClose(); onOpenReviewFeed(); }}
                                    className="w-full flex items-center justify-center gap-2 py-2 border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 text-xs font-semibold rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                                >
                                    <Zap size={13} />
                                    {vi ? 'Luyện tập ngay trong Review Feed →' : 'Practice in Review Feed →'}
                                </button>
                            </div>
                        )}
                        </>
                    )}

                    {/* History tab */}
                    {!loading && activeTab === 'history' && (
                        <div className="py-3 px-5">
                            {historyEvents.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-8">
                                    {vi ? 'Chưa có hoạt động nào.' : 'No activity yet.'}
                                </p>
                            ) : (
                                <div className="relative">
                                    {/* vertical line */}
                                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gray-100 dark:bg-slate-700" />
                                    <div className="space-y-3">
                                        {historyEvents.map((ev, i) => (
                                            <HistoryRow key={i} ev={ev} language={language} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <InfoTip
                                label={vi ? 'Tại sao cần theo dõi lịch sử?' : 'Why track learning history?'}
                                body={vi
                                    ? 'Mỗi lần bạn học — AI Check, Memory Gym, hay trả lời trong Review Feed — đều được ghi lại. Hệ thống dùng dữ liệu này để tối ưu lịch ôn tập, ưu tiên những gì bạn hay quên và giảm bớt những gì bạn đã nắm chắc.'
                                    : 'Every time you learn — AI Check, Memory Gym, or answering in Review Feed — it\'s recorded here. The system uses this to optimize your review schedule, focusing on what you forget and easing up on what you already know.'
                                }
                            />
                        </div>
                    )}
                </div>

                {/* Footer — only on Questions tab */}
                {activeTab === 'questions' && <div className="flex-shrink-0 px-5 py-4 border-t dark:border-slate-700">
                    <button
                        onClick={() => onGenerateMore(node.id)}
                        disabled={isGenerating}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-colors"
                    >
                        {isGenerating
                            ? <><Loader2 size={14} className="animate-spin" />{vi ? 'Đang tạo...' : 'Generating...'}</>
                            : <><RefreshCw size={14} />{vi ? 'Tạo lại câu hỏi' : 'Regenerate Questions'}</>
                        }
                    </button>
                    <p className="text-[10px] text-gray-400 text-center mt-2">
                        {vi
                            ? 'Sẽ thay thế toàn bộ câu hỏi hiện tại của ghi chú này'
                            : 'Replaces all current questions for this note'}
                    </p>
                </div>}
            </div>
        </div>
    );
};

// --- History row sub-component ---

const HistoryRow: React.FC<{ ev: HistoryEvent; language: Language }> = ({ ev, language }) => {
    const vi = language === 'vietnamese';

    const dot = (color: string, icon: React.ReactNode) => (
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${color}`}>
            {icon}
        </div>
    );

    if (ev.type === 'ai_check') return (
        <div className="flex items-start gap-3">
            {dot('bg-purple-100 dark:bg-purple-900/40', <Sparkles size={11} className="text-purple-500" />)}
            <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    AI Check
                    {ev.score > 0 && (
                        <span className={`ml-1.5 font-bold ${scoreColor(ev.score)}`}>{ev.score}/100</span>
                    )}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{formatRelTime(ev.timestamp, language)}</p>
            </div>
        </div>
    );

    if (ev.type === 'questions_generated') return (
        <div className="flex items-start gap-3">
            {dot('bg-amber-100 dark:bg-amber-900/40', <Zap size={11} className="text-amber-500" />)}
            <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    {ev.count} {vi ? 'câu hỏi được tạo' : 'questions generated'}
                    <span className="ml-1.5 font-normal text-gray-400">{generationLabel(ev.origin, language)}</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{formatRelTime(ev.timestamp, language)}</p>
            </div>
        </div>
    );

    if (ev.type === 'memory_gym') return (
        <div className="flex items-start gap-3">
            {dot('bg-blue-100 dark:bg-blue-900/40', <Brain size={11} className="text-blue-500" />)}
            <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    {vi ? 'Memory Gym' : 'Memory Gym'}
                    {ev.score > 0 && (
                        <span className={`ml-1.5 font-bold ${scoreColor(ev.score)}`}>{ev.score}/100</span>
                    )}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{formatRelTime(ev.timestamp, language)}</p>
            </div>
        </div>
    );

    if (ev.type === 'review_attempt') return (
        <div className="flex items-start gap-3">
            {ev.correct
                ? dot('bg-emerald-100 dark:bg-emerald-900/40', <Check size={11} className="text-emerald-500" />)
                : dot('bg-red-100 dark:bg-red-900/40', <XCircle size={11} className="text-red-400" />)
            }
            <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-1 leading-snug">{ev.questionText}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{formatRelTime(ev.timestamp, language)}</p>
            </div>
        </div>
    );

    return null;
};
